<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import type { PlayerTask } from '@/types/shared/player-config'
import { computed, ref, watch } from 'vue'
import webapp from '@/services/webapp'
import { T } from '@tolgee/vue'
import api from '@/services/api'
import { usePlayer } from '@/composables/game/player'
import { useClaimTask } from '@/composables/game/claim-task'
import { useConfig } from '@/composables/game/config'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { useAuth } from '@/composables/auth'
import { tapNationVerification, taskManualVerification } from '@/config/task'
import type { DropeeMappedTaskItem } from '@/types/task'
import TaskModalPrizeBox from '@/components/TaskModalPrizeBox.vue'

const props = defineProps<{
	show: boolean
	task: PlayerTask | DropeeMappedTaskItem
	isLoading: boolean
	error?: string
}>()
const emit = defineEmits<{
	(event: 'hide'): void,
	(event: 'resetError'): void,
	(event: 'doTask', task: PlayerTask | DropeeMappedTaskItem): void
}>()

const { profile } = useAuth()
const { updatePlayer } = usePlayer()
const { updateConfig } = useConfig()

const taskUrl = computed((): string => props.task.url?.replace(/PLAYER_RC/g, profile.value.referralCode) ?? '')
const hasAction = computed(() => !props.task.categories?.includes('no_action'))
const isCustomTask = computed(() => props.task.categories?.includes('custom'))
const actionCompletedAt = ref(props.task.actionCompletedAt)
const taskItemForClaim = computed(() => ({
  id: props.task.id,
  actionCompletedAt: actionCompletedAt.value,
  claimDelay: props.task.claimDelay,
}))
const { hasOpenedUrl, claimDelayFormattedTime } = useClaimTask(taskItemForClaim)
const hasManualVerification = computed(() => ('validations' in props.task) && props.task.validations?.includes(taskManualVerification))
const tapNationValidation = computed(() => ('validations' in props.task) && props.task.validations?.includes(tapNationVerification))
const isVerified = computed(() => props.task.isVerified)
const taskIsDone = ref(props.task.isDone ?? false)
const showError = ref(false)
const showTaskUrl = computed(() => hasAction.value && (taskIsDone.value || !hasOpenedUrl.value))
const showPendingVerification = computed(() => !showTaskUrl.value && hasManualVerification.value && !isVerified.value)

// --- Link Navigation ---
// On mobile, t.me links use native anchor tags for better Telegram UX.
// On desktop, we must use JS navigation because clicking an anchor with a t.me href
// closes the webapp before doTaskAction can complete (race condition).

function isTelegramLink(url: string): boolean {
  return url.startsWith('https://t.me/')
}

function openLink(url: string) {
  if (tapNationValidation.value) {
    window.open(url, '_blank')
  } else if (isTelegramLink(url)) {
    webapp.openTelegramLink(url)
  } else {
    webapp.openLink(url)
  }
}

const useAnchorForTelegramLink = computed(() =>
  !webapp.isNotMobile && isTelegramLink(taskUrl.value) && !isCustomTask.value
)
const anchorUrl = computed(() =>
  useAnchorForTelegramLink.value ? taskUrl.value : undefined
)
const mainButtonAnchorUrl = computed(() =>
  showTaskUrl.value ? anchorUrl.value : undefined
)

async function openTaskUrl() {
  if (!props.task) return

  if (isCustomTask.value) {
    if ('onSecondaryClick' in props.task) {
      props.task.onSecondaryClick?.()
    }
  } else if (taskUrl.value && !useAnchorForTelegramLink.value) {
    openLink(taskUrl.value)
  }

  if (taskIsDone.value) return
  if (props.error) {
    setTimeout(() => emit('resetError'), 200)
    return
  }
  const { config, playerStats } = await api.doTaskAction(props.task.id)
  const taskFromResponse = config.tasks.find(task => task.id === props.task.id)
  actionCompletedAt.value = taskFromResponse?.actionCompletedAt
  updateConfig(config)
  updatePlayer(playerStats)
}

function openExternalUrl(url: string) {
  if (isCustomTask.value && 'onSecondaryClick' in props.task) {
    props.task.onSecondaryClick?.()
    return
  }
  if (!useAnchorForTelegramLink.value) {
    openLink(url)
  }
}

function check() {
  if (!props.task) return
  if (hasAction.value && (!hasOpenedUrl.value || taskIsDone.value)) {
    openTaskUrl()
    return
  }
  emit('doTask', props.task)
}

watch(() => props.task, (task, oldValue) => {
  if (!task) return

  if (!oldValue) {
    taskIsDone.value = task.isDone
  }
}, { immediate: true })

watch(() => props.error, error => {
  if (!props.show) return
  showError.value = !!error
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
  >
    <div class="flex flex-col gap-y-8">
      <div
        v-if="task"
        class="w-full flex flex-col items-center gap-y-6"
      >
        <img
          class="rounded-[2px] w-[120px] h-[120px] min-w-[120px] object-cover"
          :src="task.imageUrl"
        >
        <div
          class="text-center text-2xl font-semibold -tracking-[0.48px] text-light-white"
        >
          {{ task.title }}
        </div>
        <div
          v-if="task.description"
          class="text-center text-sm font-medium -tracking-[0.48px] text-light-white max-w-[379px] whitespace-pre-line"
        >
          {{ task.description }}
        </div>
        <BaseButton
          v-if="!!task.ctaAfterDone && (!showTaskUrl || claimDelayFormattedTime || showPendingVerification)"
          style-type="secondary"
          button-class="px-4 py-2 h-[44px]"
          :url="anchorUrl"
          @click="openExternalUrl(taskUrl)"
        >
          {{ task.ctaAfterDone }}
          <template #icon>
            <ArrowRightIcon />
          </template>
        </BaseButton>

        <TaskModalPrizeBox
          :reward="task.reward as number"
          :spins="task.spins as number"
          :tickets="task.tickets as number"
        />
      </div>
      <div class="w-full flex flex-col gap-y-1">
        <div
          v-if="showPendingVerification"
          class="text-center pb-3 text-white/60 text-base font-medium"
        >
          <T
            key-name="quests.manual_verification_info1"
            default-value="This task requires manual verification."
          />
          <br>
          <T
            key-name="quests.manual_verification_info1"
            default-value="We'll review this task shortly."
          />
        </div>
        <!--        <div-->
        <!--          v-if="error && showError"-->
        <!--          class="text-center text-sm font-medium text-red-danger"-->
        <!--        >-->
        <!--          {{ error }}-->
        <!--        </div>-->
        <BaseButton
          class="w-full"
          :loading="isLoading"
          :disabled="showPendingVerification || !!claimDelayFormattedTime"
          :url="mainButtonAnchorUrl"
          @click="check"
        >
          <template v-if="showTaskUrl">
            {{ taskIsDone ? task.ctaAfterDone : (task.cta ?? 'Open') }}
          </template>
          <T
            v-else-if="showPendingVerification"
            key-name="quests.pending_review"
            default-value="Pending review"
          />
          <T
            v-else
            key-name="quests.claim"
            default-value="Claim"
          />
          <span
            v-if="claimDelayFormattedTime"
            class="ml-2"
            style="font-variant: tabular-nums;"
          >{{ claimDelayFormattedTime }}</span>
        </BaseButton>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>
